// FamilyAgenda — Feb 19, 2026 (Randy Photo #2)
// "What's happening" section inside Family View. Backed by
// /api/calendar/{silo_id}/family-agenda. Kid-role callers see their own +
// everyone-visible items only. Parent-role callers get a filter dropdown:
// "Just me" (default) / "Everybody" / any household member by name.
import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarCheck, CaretDown, CaretUp } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

const KIND_EMOJI = { event: "📅", task: "✅", routine: "🔁", ping: "🔔" };

function dayLabel(iso, todayIso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  const t = new Date(todayIso + "T00:00:00");
  const diff = Math.round((d - t) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function fmt12(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10); const period = h >= 12 ? "PM" : "AM";
  return `${((h % 12) || 12)}:${mm} ${period}`;
}

export default function FamilyAgenda({ siloId, members = [], isParentRole = false, defaultOpen = true }) {
  // pt89U Stage-C — Re-Themer hooks.
  const tHeader   = useThemeLabel(T.THEME_KEY_FAMILYVIEW_WHATS_HAPPENING);
  const tEmpty    = useThemeLabel(T.THEME_KEY_FAMILYVIEW_WHATS_EMPTY);
  const tMe       = useThemeLabel(T.THEME_KEY_FAMILYVIEW_FILTER_ME);
  const tEveryone = useThemeLabel(T.THEME_KEY_FAMILYVIEW_FILTER_EVERYONE);
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(`mysilo_family_agenda_open_${siloId}`); return v === null ? defaultOpen : v === "1"; }
    catch { return defaultOpen; }
  });
  useEffect(() => {
    try { localStorage.setItem(`mysilo_family_agenda_open_${siloId}`, open ? "1" : "0"); } catch { /* ignore */ }
  }, [open, siloId]);

  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem(`mysilo_family_agenda_filter_${siloId}`) || "me"; }
    catch { return "me"; }
  });
  useEffect(() => {
    try { localStorage.setItem(`mysilo_family_agenda_filter_${siloId}`, filter); } catch { /* ignore */ }
  }, [filter, siloId]);

  const [data, setData] = useState({ occurrences: [], events: [], today: null });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/calendar/${siloId}/family-agenda`, { params: { days: 7, filter } });
      setData(r.data || { occurrences: [], events: [] });
    } catch { setData({ occurrences: [], events: [] }); }
    finally { setLoading(false); }
  }, [siloId, filter]);
  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries((data.events || []).map((e) => [e.id, e])), [data.events]);
  const groupedByDay = useMemo(() => {
    const g = {};
    for (const o of data.occurrences || []) {
      (g[o.date] = g[o.date] || []).push(o);
    }
    return Object.entries(g);
  }, [data.occurrences]);

  return (
    <section className="bg-white border-2 border-[#DCEAF7] rounded-2xl p-4" data-testid="familyview-whats-happening">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="familyview-whats-happening-toggle"
        className={`w-full flex items-center gap-2 text-left ${open ? "mb-3" : ""}`}
        aria-expanded={open}
      >
        <CalendarCheck size={16} weight="duotone" className="text-[#4A5D8E]" />
        <h2 className="font-serif-display text-base text-[#1C1B1A] flex-1">{tHeader}</h2>
        {isParentRole && (
          <select
            value={filter}
            onChange={(e) => { e.stopPropagation(); setFilter(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            data-testid="familyview-whats-happening-filter"
            className="text-[11px] px-2 py-1 border hairline rounded bg-white text-[#1C1B1A]"
          >
            <option value="me">{tMe}</option>
            <option value="everyone">{tEveryone}</option>
            {members.filter((m) => m.user_id).map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.emoji || (m.is_kid ? "🙂" : "👤")} {m.name}</option>
            ))}
          </select>
        )}
        {open ? <CaretUp size={12} weight="bold" className="text-[#6B6A68]" /> : <CaretDown size={12} weight="bold" className="text-[#6B6A68]" />}
      </button>
      {open && (
        <>
          {loading ? (
            <p className="text-sm text-[#6B6A68] italic" data-testid="familyview-whats-happening-loading">Loading…</p>
          ) : groupedByDay.length === 0 ? (
            <p className="text-sm text-[#6B6A68] italic" data-testid="familyview-whats-happening-empty">{tEmpty}</p>
          ) : (
            <div className="space-y-2">
              {groupedByDay.map(([date, items]) => (
                <div key={date}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1">{dayLabel(date, data.today)}</div>
                  {items.map((o, i) => {
                    const ev = byId[o.event_id] || {};
                    return (
                      <div key={`${date}-${i}`} className="flex items-center gap-2 text-sm text-[#1C1B1A] py-0.5" data-testid={`familyview-agenda-row-${o.event_id}-${date}`}>
                        <span>{KIND_EMOJI[o.kind] || "📌"}</span>
                        <span className="flex-1 truncate">{ev.title}</span>
                        {o.assignee_name && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] shrink-0" title={`Assigned to ${o.assignee_name}`}>
                            {o.assignee_name}
                          </span>
                        )}
                        {o.start_time && <span className="text-[11px] text-[#6B6A68] shrink-0">{fmt12(o.start_time)}</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
